import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Button, Callout, Checkbox, FormField } from "@promptgatewaysadmin/ui";
import { useAuth, authErrorKey } from "../../auth/AuthContext.js";
import { usePortalClient } from "../../api/PortalClientProvider.js";
import { provisionAccount } from "../../api/generated/identity.generated.js";
import { writeFunnel } from "../../auth/persistence.js";
import { errorCopy } from "../../copy/errors.js";
import { config } from "../../config.js";
import { toPortalError } from "../../components/states.js";
import { isKnownPlan, isKnownInterval } from "../../pricing.js";

/**
 * /signup
 *
 * The one non-obvious thing on this screen is `intent`.
 *
 *   ?plan=individual (or no plan param) -> intent: "personal"  — provision a single-seat org
 *   ?plan=team                          -> intent: "none"      — NO org yet
 *
 * A team signup deliberately creates no org here: the org is created after verification, so
 * we never stand one up for an address nobody has proven they control. That is also why
 * RequireVerified sits above RequireOrg.
 *
 * [CORRECTION] The design specs call this field `orgMode`. The merged contract's field is
 * `intent`, and every schema is additionalProperties:false — `orgMode` would have been
 * rejected with a 400 on the very first real signup.
 */
export function Signup() {
  const auth = useAuth();
  const client = usePortalClient();
  const navigate = useNavigate();
  const [params] = useSearchParams();

  const plan = isKnownPlan(params.get("plan")) ? params.get("plan")! : null;
  const interval = isKnownInterval(params.get("interval")) ? params.get("interval")! : null;

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [terms, setTerms] = useState(false);
  const [busy, setBusy] = useState(false);
  const [errKey, setErrKey] = useState<string | null>(null);

  // NOTE: the `intent` VALUE is still "personal". It names the org shape (a single-seat org),
  // not the plan code — see ProvisionAccountRequest.intent in identity.v1.yaml.
  const intent: "personal" | "none" = plan === "team" ? "none" : "personal";

  async function provisionAndGo() {
    await client.get(provisionAccount, "auth.signup", {
      body: name.trim() ? { intent, displayName: name.trim() } : { intent },
    });
    // Held in sessionStorage, not the URL: no commercial intent in a history entry, and
    // losing it would drop a customer who already chose onto a generic pricing screen.
    writeFunnel({ plan: plan ?? undefined, interval: interval ?? undefined });
    navigate("/verify", { replace: true });
  }

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!terms || busy) return;
    setBusy(true);
    setErrKey(null);
    try {
      await auth.signUpWithPassword(email, password);
      await provisionAndGo();
    } catch (err) {
      setErrKey(keyFor(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <h1>Create your account</h1>
      <p className="pg-auth__sub">14 days free. No credit card.</p>

      {errKey && (
        <Callout tone="danger" role="alert">
          {errorCopy(errKey, { email }).body}
        </Callout>
      )}

      <form onSubmit={submit} noValidate>
        <FormField
          label="Name"
          type="text"
          autoComplete="name"
          value={name}
          onChange={(e) => setName(e.currentTarget.value)}
        />
        <FormField
          label="Work email"
          type="email"
          required
          autoComplete="email"
          value={email}
          onChange={(e) => setEmail(e.currentTarget.value)}
        />
        <FormField
          label="Password"
          type="password"
          required
          // Never block paste, and always allow a manager to fill it. A portal that fights
          // password managers trains users into worse passwords.
          autoComplete="new-password"
          help="At least 8 characters, including a number."
          value={password}
          onChange={(e) => setPassword(e.currentTarget.value)}
        />

        <Checkbox
          label={
            <>
              I am at least 14 years old and agree to the{" "}
              <a href={`${config.siteOrigin}/legal/terms`} target="_blank" rel="noopener noreferrer">
                Terms
              </a>{" "}
              and{" "}
              <a href={`${config.siteOrigin}/legal/privacy`} target="_blank" rel="noopener noreferrer">
                Privacy Policy
              </a>
            </>
          }
          checked={terms}
          onChange={(e) => setTerms(e.currentTarget.checked)}
        />

        <p className="pg-auth__note">We'll email you a few tips to get started. Unsubscribe anytime.</p>

        {/* aria-disabled with a stated reason, never silently inert. */}
        <Button
          type="submit"
          block
          disabled={busy}
          aria-disabled={!terms || undefined}
          aria-describedby={!terms ? "terms-reason" : undefined}
        >
          {busy ? "Creating your account…" : "Start free trial"}
        </Button>
        {!terms && (
          <p id="terms-reason" className="pg-disabled-reason">
            Confirm you're 14 or older and accept the Terms to continue.
          </p>
        )}
      </form>

      <p className="pg-auth__alt">
        Already have an account? <Link to="/login">Log in</Link>
      </p>
    </>
  );
}

function keyFor(err: unknown): string {
  const code = (err as { code?: string })?.code;
  if (typeof code === "string" && code.startsWith("auth/")) return authErrorKey(code);
  return toPortalError(err)?.copyKey ?? "error.auth.unavailable";
}
